import { addDays, warsawInstant, warsawParts } from '../events/labels';

export const STAGES_RESOURCE = 'schedule.stages';
export const STAGE_TYPES_RESOURCE = 'schedule.settings';
export const TASKS_RESOURCE = 'schedule.tasks';
export const SCHEDULE_KEY = ['schedule'] as const;

export const TASK_PRIORITIES = { niski: 'Niski', normalny: 'Normalny', pilny: 'Pilny' } as const;
export type TaskPriority = keyof typeof TASK_PRIORITIES;
export const TASK_KINDS = { zwykle: 'Zwykłe', zwrot_wypozyczenia: 'Zwrot wypożyczenia' } as const;
export type TaskKind = keyof typeof TASK_KINDS;

export type StageType = { id: string; name: string; sort: number; version: number };

/** A stage as the API sends it: fields the role may not see are absent (rule 7a). */
export type Stage = {
  id: string;
  eventId: string;
  version: number;
  stageTypeId?: string;
  stageTypeName?: string;
  stageTypeDeleted?: boolean;
  startsAt?: string;
  endsAt?: string | null;
  personId?: string | null;
  personName?: string | null;
  notes?: string;
  /** Spec 2026-10-22-mapy-google §5: the "Wyjazd" the app keeps in line with the travel time (not changed by hand). */
  automatic?: boolean;
};

/** A task as the API sends it (fields hidden from the role are absent). */
export type Task = {
  id: string;
  eventId: string | null;
  version: number;
  title?: string;
  dueAt?: string;
  priority?: TaskPriority;
  kind?: TaskKind;
  contractorId?: string | null;
  contractorName?: string | null;
  personId?: string | null;
  personName?: string | null;
  done?: boolean;
  doneAt?: string | null;
  fromFunction: boolean;
  rescheduleCount?: number;
  lastReason?: string | null;
  /** On "Do zrobienia" and the returns list: */
  eventName?: string;
  canOpen?: boolean;
  overdue?: boolean;
};

const short = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;

/** "7.11 09:00" or, on another year than `today`'s, "7.11.2027 09:00". */
export function instantText(iso: string, today?: string): string {
  const { day, time } = warsawParts(iso);
  const year = today && today.slice(0, 4) !== day.slice(0, 4) ? `.${day.slice(0, 4)}` : '';
  return `${short(day)}${year} ${time}`;
}

/** A stage's time: "7.11 08:00–09:30", "7.11 22:00 – 8.11 01:00" or only the start. */
export function stageTimeText(startsAt: string, endsAt: string | null | undefined): string {
  if (!endsAt) return instantText(startsAt);
  const s = warsawParts(startsAt);
  const e = warsawParts(endsAt);
  return s.day === e.day ? `${short(s.day)} ${s.time}–${e.time}` : `${short(s.day)} ${s.time} – ${short(e.day)} ${e.time}`;
}

/** Day + "HH:mm" of a form (Warsaw time) → an instant; an end not after the start goes to the next day (night). */
export function stageInstants(day: string, from: string, to: string): { startsAt: string; endsAt: string | null } {
  const startsAt = warsawInstant(day, from);
  if (to === '') return { startsAt, endsAt: null };
  return { startsAt, endsAt: warsawInstant(to <= from ? addDays(day, 1) : day, to) };
}

/** "Do zrobienia": the open tasks (overdue ones first come by due time anyway) and the recently done ones. */
export function splitTasks<T extends { done?: boolean }>(list: T[]): { open: T[]; done: T[] } {
  return { open: list.filter((t) => !t.done), done: list.filter((t) => t.done) };
}

/** Whether the task is past its due time and not done (the server says it on "Do zrobienia"; the tab counts it). */
export const isOverdue = (t: Pick<Task, 'done' | 'dueAt' | 'overdue'>, now = Date.now()): boolean =>
  t.overdue ?? (!t.done && t.dueAt !== undefined && new Date(t.dueAt).getTime() < now);
